/* eslint-disable prefer-destructuring */
import { EvaluationCriteria } from '@src/main/modules/reports/shared/domain/evaluation.interface';
import { toEndOfDayUTC, toLocalISODate, toStartOfDayUTC } from '@src/main/shared/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTableFilterMeta } from 'primereact/datatable';

export type EvaluationFilterMeta = DataTableFilterMeta & {
  global: { value: string | null; matchMode: FilterMatchMode };
  carrierCode: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  flightNumber: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  departureAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  arrivalAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  aircraftRegistration: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  supervisorEmployeeId?: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  evaluatedEmployeeId?: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  purserEmployeeId?: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  flightDate: {
    operator: FilterOperator;
    constraints: Array<{ value: [Date | null, Date | null]; matchMode: FilterMatchMode.BETWEEN }>;
  };
  receptionDate: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null] | null;
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };
};

export const mapEvaluationFiltersToCriteria = (
  filters: EvaluationFilterMeta,
  emptyCriteria: EvaluationCriteria,
): EvaluationCriteria => {
  const criteria: EvaluationCriteria = { ...emptyCriteria };

  const normalize = (value?: string | null) =>
    typeof value === 'string' ? value.toUpperCase().trim() : value;

  const fieldMap: Record<string, keyof EvaluationCriteria> = {
    carrierCode: 'carrierCode',
    flightNumber: 'flightNumber',
    departureAirport: 'airportFrom',
    arrivalAirport: 'airportTo',
    aircraftRegistration: 'aircraftRegistration',
    supervisorEmployeeId: 'supervisorEmployeeId',
    evaluatedEmployeeId: 'evaluatedEmployeeId',
    purserEmployeeId: 'purserEmployeeId',
  };

  Object.keys(fieldMap).forEach((filterKey) => {
    const criteriaKey = fieldMap[filterKey];
    const filter = filters[filterKey as keyof EvaluationFilterMeta];
    const value = filter && 'constraints' in filter ? filter.constraints?.[0]?.value : null;
    if (value) {
      (criteria[criteriaKey] as string) = normalize(value) as string;
    }
  });

  const flightDateRange = filters.flightDate?.constraints?.[0]?.value;
  if (flightDateRange) {
    const [start, end] = flightDateRange;
    if (start) criteria.flightSince = toLocalISODate(start);
    if (end) criteria.flightUntil = toLocalISODate(end);
  }

  const receptionDateRange = filters.receptionDate?.constraints?.[0]?.value;
  if (receptionDateRange && (receptionDateRange[0] || receptionDateRange[1])) {
    const [start, end] = receptionDateRange;
    if (start) criteria.receptionSince = toStartOfDayUTC(start);
    if (end) criteria.receptionUntil = toEndOfDayUTC(end);
  }

  return criteria;
};
